import React from 'react';
import {
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  Paper,
  Link
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Business as BusinessIcon,
  Assessment as AssessmentIcon,
  AttachMoney as MoneyIcon,
  Code as CodeIcon,
  Security as SecurityIcon,
  School as SchoolIcon,
  Fingerprint as BiometricsIcon,
  Public as PublicIcon,
  Group as CollaborationIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

const DHSICEContractsDetail: React.FC = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/admin/dev-technology-contracts');
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleBack}
          sx={{ mr: 2 }}
          aria-label="Back to Dev Technology Contracts"
        >
          Back
        </Button>
        <Box>
          <Typography variant="h4" sx={{ color: 'primary.main', fontWeight: 'bold' }}>
            Additional DHS/ICE Contracts Portfolio
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            $209 Million+ - Identity, Biometrics, and Student Systems
          </Typography>
        </Box>
      </Box>

      {/* Portfolio Overview */}
      <Card sx={{ mb: 3, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Contract Portfolio Overview
          </Typography>
          <Typography variant="body1" paragraph>
            Beyond CBP cargo and trade systems, Dev Technology Group supports a diverse portfolio of DHS and ICE 
            contracts totaling over $209 million. This portfolio spans identity management, biometric systems, student 
            and exchange visitor programs, collaboration services, and TSA security systems. These contracts demonstrate 
            Dev Technology's broad capabilities across homeland security mission areas, from international student tracking 
            to biometric identity verification and inter-agency collaboration platforms.
          </Typography>
        </CardContent>
      </Card>

      {/* Portfolio Summary */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <MoneyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Contract Portfolio Summary
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #F57C00' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                $209M+
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total Portfolio Value
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                DHS/ICE contracts
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #1976D2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                4
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Major Contracts
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Active awards
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                3
              </Typography>
              <Typography variant="body2" color="text.secondary">
                DHS Agencies
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                ICE, TSA, CBP
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card sx={{ textAlign: 'center', border: '2px solid #7B1FA2' }}>
            <CardContent>
              <Typography variant="h3" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                2025
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Recent Awards
              </Typography>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                Latest contracts
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Major Contracts */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <BusinessIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Major Contract Awards
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SchoolIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  ICE SEVIS Support Contract
                </Typography>
              </Box>
              <Chip label="$64 Million" color="primary" sx={{ mb: 2 }} />
              <Typography variant="body2" paragraph>
                <strong>Award ID:</strong> 70B04C25F00000086
              </Typography>
              <Typography variant="body2" paragraph>
                Five-year contract for Student and Exchange Visitor Information System (SEVIS) support.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>System:</strong> SEVIS - Student/Exchange Visitor tracking</Typography></li>
                <li><Typography variant="body2"><strong>Users:</strong> Schools, students, exchange programs</Typography></li>
                <li><Typography variant="body2"><strong>Scope:</strong> Application development, O&M, modernization</Typography></li>
                <li><Typography variant="body2"><strong>Duration:</strong> 5 years</Typography></li>
                <li><Typography variant="body2"><strong>Agency:</strong> ICE Student and Exchange Visitor Program (SEVP)</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #7B1FA2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <BiometricsIcon sx={{ fontSize: 40, color: '#7B1FA2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                  ICE Biometrics and IDM Contract
                </Typography>
              </Box>
              <Chip label="$63 Million" color="primary" sx={{ mb: 2 }} />
              <Typography variant="body2" paragraph>
                <strong>Award Date:</strong> 2025
              </Typography>
              <Typography variant="body2" paragraph>
                Biometrics and identity management for international student program.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Focus:</strong> Biometric identity verification</Typography></li>
                <li><Typography variant="body2"><strong>Systems:</strong> Identity management platforms</Typography></li>
                <li><Typography variant="body2"><strong>Technology:</strong> Facial recognition, fingerprint matching</Typography></li>
                <li><Typography variant="body2"><strong>Integration:</strong> SEVIS and border systems</Typography></li>
                <li><Typography variant="body2"><strong>Security:</strong> Enhanced identity verification</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CollaborationIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  ICE Collaboration Services
                </Typography>
              </Box>
              <Chip label="$82 Million" color="primary" sx={{ mb: 2 }} />
              <Typography variant="body2" paragraph>
                <strong>Type:</strong> Task Order
              </Typography>
              <Typography variant="body2" paragraph>
                Collaboration and communication services for ICE operations.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Platforms:</strong> Collaboration and communication tools</Typography></li>
                <li><Typography variant="body2"><strong>Services:</strong> SharePoint, Teams, enterprise collaboration</Typography></li>
                <li><Typography variant="body2"><strong>Support:</strong> 24/7 operations and maintenance</Typography></li>
                <li><Typography variant="body2"><strong>Users:</strong> ICE personnel nationwide</Typography></li>
                <li><Typography variant="body2"><strong>Integration:</strong> DHS enterprise systems</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ fontSize: 40, color: '#F57C00', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                  DHS TSA FAST 2.5 Contract
                </Typography>
              </Box>
              <Chip label="$170 Million" color="primary" sx={{ mb: 2 }} />
              <Typography variant="body2" paragraph>
                <strong>Program:</strong> FAST (Flexible Agile Support for the TSA)
              </Typography>
              <Typography variant="body2" paragraph>
                Agile development and support for TSA security systems.
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Agency:</strong> Transportation Security Administration (TSA)</Typography></li>
                <li><Typography variant="body2"><strong>Methodology:</strong> Agile, DevSecOps, cloud-native</Typography></li>
                <li><Typography variant="body2"><strong>Systems:</strong> TSA security and screening systems</Typography></li>
                <li><Typography variant="body2"><strong>Services:</strong> Development, modernization, operations</Typography></li>
                <li><Typography variant="body2"><strong>Mission:</strong> Aviation and transportation security</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Contract Details Table */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <AssessmentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Contract Details & Timeline
      </Typography>

      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Contract</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Value</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Agency</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Award Year</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <TableRow>
              <TableCell><strong>TSA FAST 2.5</strong></TableCell>
              <TableCell>$170M</TableCell>
              <TableCell>TSA</TableCell>
              <TableCell>Recent</TableCell>
              <TableCell><Chip label="Active" color="success" size="small" /></TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>ICE Collaboration Services</strong></TableCell>
              <TableCell>$82M</TableCell>
              <TableCell>ICE</TableCell>
              <TableCell>Recent</TableCell>
              <TableCell><Chip label="Active" color="success" size="small" /></TableCell>
            </TableRow>
            <TableRow>
              <TableCell><strong>ICE SEVIS Support</strong></TableCell>
              <TableCell>$64M</TableCell>
              <TableCell>ICE SEVP</TableCell>
              <TableCell>2025</TableCell>
              <TableCell><Chip label="Active" color="success" size="small" /></TableCell>
            </TableRow>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell><strong>ICE Biometrics/IDM</strong></TableCell>
              <TableCell>$63M</TableCell>
              <TableCell>ICE</TableCell>
              <TableCell>2025</TableCell>
              <TableCell><Chip label="Active" color="success" size="small" /></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* Core Capabilities */}
      <Typography variant="h5" gutterBottom sx={{ color: 'primary.main', fontWeight: 'bold', mt: 4, mb: 3 }}>
        <CodeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Core Capabilities Across Portfolio
      </Typography>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #1976D2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SecurityIcon sx={{ fontSize: 40, color: '#1976D2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#1976D2', fontWeight: 'bold' }}>
                  Identity & Biometrics
                </Typography>
              </Box>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Biometric Systems:</strong> Facial recognition, fingerprint matching</Typography></li>
                <li><Typography variant="body2"><strong>Identity Management:</strong> IAM, authentication, authorization</Typography></li>
                <li><Typography variant="body2"><strong>SEVIS Integration:</strong> Student identity verification</Typography></li>
                <li><Typography variant="body2"><strong>Multi-Factor Authentication:</strong> Enhanced security controls</Typography></li>
                <li><Typography variant="body2"><strong>Access Control:</strong> RBAC, permissions management</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #2E7D32' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <SchoolIcon sx={{ fontSize: 40, color: '#2E7D32', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#2E7D32', fontWeight: 'bold' }}>
                  Student & Exchange Programs
                </Typography>
              </Box>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>SEVIS Platform:</strong> Student/exchange visitor tracking</Typography></li>
                <li><Typography variant="body2"><strong>School Certification:</strong> Educational institution management</Typography></li>
                <li><Typography variant="body2"><strong>Compliance Monitoring:</strong> Status tracking and reporting</Typography></li>
                <li><Typography variant="body2"><strong>Data Management:</strong> Student records and documentation</Typography></li>
                <li><Typography variant="body2"><strong>Portal Development:</strong> User-friendly interfaces</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #F57C00' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CollaborationIcon sx={{ fontSize: 40, color: '#F57C00', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#F57C00', fontWeight: 'bold' }}>
                  Collaboration Services
                </Typography>
              </Box>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Enterprise Collaboration:</strong> SharePoint, Teams, Office 365</Typography></li>
                <li><Typography variant="body2"><strong>Communication Tools:</strong> Video conferencing, messaging</Typography></li>
                <li><Typography variant="body2"><strong>Document Management:</strong> Secure file sharing and storage</Typography></li>
                <li><Typography variant="body2"><strong>Workflow Automation:</strong> Business process automation</Typography></li>
                <li><Typography variant="body2"><strong>24/7 Support:</strong> Operations and maintenance</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card sx={{ height: '100%', border: '2px solid #7B1FA2' }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <CodeIcon sx={{ fontSize: 40, color: '#7B1FA2', mr: 2 }} />
                <Typography variant="h6" sx={{ color: '#7B1FA2', fontWeight: 'bold' }}>
                  Agile Development & DevSecOps
                </Typography>
              </Box>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 1 } }}>
                <li><Typography variant="body2"><strong>Agile Methodology:</strong> Scrum, Kanban, SAFe frameworks</Typography></li>
                <li><Typography variant="body2"><strong>DevSecOps:</strong> CI/CD pipelines, automated testing</Typography></li>
                <li><Typography variant="body2"><strong>Cloud-Native:</strong> AWS, Azure, containerization</Typography></li>
                <li><Typography variant="body2"><strong>Security Integration:</strong> Security scanning, compliance</Typography></li>
                <li><Typography variant="body2"><strong>Continuous Delivery:</strong> Rapid deployment cycles</Typography></li>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Mission Impact */}
      <Card sx={{ mb: 4, bgcolor: '#e3f2fd', border: '2px solid #1976D2' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: '#1976D2' }}>
            Mission Impact Across DHS
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                ICE SEVP Mission:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Track 1.5M+ international students</Typography></li>
                <li><Typography variant="body2">Monitor 8,000+ certified schools</Typography></li>
                <li><Typography variant="body2">Ensure compliance with visa requirements</Typography></li>
                <li><Typography variant="body2">Support national security objectives</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                ICE Operations:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Enhanced collaboration capabilities</Typography></li>
                <li><Typography variant="body2">Secure communication platforms</Typography></li>
                <li><Typography variant="body2">Biometric identity verification</Typography></li>
                <li><Typography variant="body2">Improved operational efficiency</Typography></li>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                TSA Security:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Aviation security systems</Typography></li>
                <li><Typography variant="body2">Screening technology support</Typography></li>
                <li><Typography variant="body2">Agile development for rapid updates</Typography></li>
                <li><Typography variant="body2">Transportation security enhancement</Typography></li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Reference Links */}
      <Card sx={{ mb: 4, bgcolor: '#f0f4f8' }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Contract References & Documentation
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                ICE Contracts:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li>
                  <Link href="https://orangeslices.ai/dev-technology-group-beats-out-17-to-win-64m-dhs-ice-sevp-student-and-exchange-visitor-information-systems-sevis-support-contract/" target="_blank" rel="noopener">
                    <Typography variant="body2">$64M ICE SEVIS Support Contract</Typography>
                  </Link>
                </li>
                <li>
                  <Link href="https://www.biometricupdate.com/202502/ice-awards-63m-biometrics-and-idm-contract-for-international-student-program" target="_blank" rel="noopener">
                    <Typography variant="body2">$63M Biometrics and IDM Contract</Typography>
                  </Link>
                </li>
                <li>
                  <Link href="https://www.hstoday.us/industry/industry-news/dev-technology-group-secures-82m-dhs-ice-collaboration-services-task/" target="_blank" rel="noopener">
                    <Typography variant="body2">$82M ICE Collaboration Services</Typography>
                  </Link>
                </li>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                TSA & Additional Resources:
              </Typography>
              <Box component="ul" sx={{ pl: 2, '& li': { mb: 0.5 } }}>
                <li>
                  <Link href="https://devtechnology.com/blog/dev-technology-group-awarded-170m-dhs-tsa-fast-2-5-contract/" target="_blank" rel="noopener">
                    <Typography variant="body2">$170M DHS TSA FAST 2.5 Contract</Typography>
                  </Link>
                </li>
                <li>
                  <Link href="https://www.usaspending.gov/award/CONT_AWD_70B04C25F00000086_7014_47QTCA19D00JT_4732" target="_blank" rel="noopener">
                    <Typography variant="body2">USASpending.gov - SEVIS Contract Details</Typography>
                  </Link>
                </li>
                <li>
                  <Link href="https://www.usaspending.gov/recipient/d768b60a-beaa-2e46-7aa5-6df63e232bb2-C/latest" target="_blank" rel="noopener">
                    <Typography variant="body2">Dev Technology Group - Recipient Profile</Typography>
                  </Link>
                </li>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Container>
  );
};

export default DHSICEContractsDetail;
